import { Flex, HStack, Text, VStack, Spinner } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { useNavigate } from 'react-router-dom';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

export default function StatCard({
  backgroundColour,
  title,
  titleColour,
  description,
  descriptionColour,
  value,
  valueColour,
  onClick,
  to,
}: {
  backgroundColour: string;
  title: string;
  titleColour: string;
  description: string;
  descriptionColour: string;
  value?: number | string;
  valueColour: string;
  onClick?: () => void;
  to?: string;
}) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (to !== undefined) {
      navigate(to);
    }

    if (onClick) {
      onClick();
    }
  };

  const hasInteraction = onClick !== undefined || to !== undefined;

  return (
    <VStack
      borderRadius="2xl"
      width={'full'}
      {...(hasInteraction && {
        onClick: handleClick,
        as: 'button',
        variant: 'ghost',
        _hover: { backgroundColor: backgroundColour },
        role: to !== undefined ? 'link' : 'button',
      })}
      aria-label={t`View all ${title} details`}
      py="1"
      pl="3"
      pr="2"
      gap="1"
      flex="1"
      minHeight={pxToRem(84)}
      alignItems="flex-start"
      justifyContent="space-between"
      backgroundColor={backgroundColour}
    >
      <HStack w="full" pt="1" gap="2" justifyContent="space-between">
        <Text
          w="full"
          textAlign="left"
          fontSize="xs"
          lineHeight="4"
          letterSpacing="0%"
          verticalAlign="bottom"
          fontWeight="semibold"
          color={titleColour}
        >
          {title}
        </Text>
      </HStack>
      <HStack
        w="full"
        minH={pxToRem(32)}
        gap="2"
        alignItems="flex-end"
        justifyContent="space-between"
      >
        <Flex flex="1" pr="2" pb="1.5" gap="1">
          <Text
            fontSize="2xs"
            verticalAlign="bottom"
            textAlign="left"
            lineHeight="3"
            fontWeight="medium"
            color={descriptionColour}
          >
            {description}
          </Text>
        </Flex>
        <Flex pr="1" gap="2.5">
          {value === undefined ? (
            <Spinner my={2} mx={1} size="sm" />
          ) : (
            <Text
              fontSize="2xl"
              lineHeight="8"
              fontWeight="medium"
              color={valueColour}
              textAlign="right"
              verticalAlign="middle"
              letterSpacing="0%"
            >
              {value}
            </Text>
          )}
        </Flex>
      </HStack>
    </VStack>
  );
}
